import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Search, Calendar, Filter, Download, Eye, Ticket } from 'lucide-react';
import api from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../lib/currencyUtils';

const statusColors = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  redeemed: 'bg-gray-100 text-gray-600 border-gray-200',
  suspended: 'bg-red-50 text-red-600 border-red-200',
  expired: 'bg-orange-50 text-orange-600 border-orange-200',
  partial: 'bg-blue-50 text-blue-600 border-blue-200',
  blocked: 'bg-red-100 text-red-700 border-red-300',
};

const statusLabels = {
  active: 'Active',
  redeemed: 'Fully Redeemed',
  suspended: 'Suspended',
  expired: 'Expired',
  partial: 'Partially Redeemed',
  blocked: 'Blocked',
};

export default function VouchersTab({ onViewDetail, initialFilters }) {
  const { currency: activeCurrency, formatShortDate: formatDate } = useCurrency();
  const [vouchers, setVouchers] = useState([]);
  const [stores, setStores] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(initialFilters?.status || '');
  const [filterTypeId, setFilterTypeId] = useState('');
  const [filterStoreId, setFilterStoreId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    if (filterTypeId) params.set('type_id', filterTypeId);
    if (filterStoreId) params.set('store_id', filterStoreId);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (searchTerm) params.set('search', searchTerm);
    params.set('skip', String(page * PAGE_SIZE));
    params.set('limit', String(PAGE_SIZE));
    const [vRes, sRes] = await Promise.all([
      api.get(`/api/voucher/admin/vouchers?${params}`).catch(() => ({ data: { vouchers: [], total: 0 } })),
      api.get('/api/store/admin/stores').catch(() => ({ data: [] })),
    ]);
    setVouchers(vRes.data.vouchers);
    setTotal(vRes.data.total);
    setStores(sRes.data.filter(s => s.is_active));
    setLoading(false);
  }, [filterStatus, filterTypeId, filterStoreId, dateFrom, dateTo, searchTerm, page]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = () => { setPage(0); load(); };
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterTypeId) params.set('type_id', filterTypeId);
      if (filterStoreId) params.set('store_id', filterStoreId);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (searchTerm) params.set('search', searchTerm);
      const res = await api.get(`/api/voucher/admin/vouchers/export?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vouchers_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  const getDisplayStatus = (v) => {
    if (v.status === 'active' && v.balance !== undefined && v.balance < v.amount) return 'partial';
    return v.status;
  };

  return (
    <div data-testid="vouchers-tab">
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1">Search</label>
            <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 bg-gray-50">
              <Search size={14} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Code, name or email..."
                className="outline-none bg-transparent text-sm text-gray-700 w-full"
                data-testid="search-voucher"
              />
            </div>
          </div>
          {/* Status */}
          <div className="w-40">
            <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none bg-gray-50"
              data-testid="filter-status"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="partial">Partially Redeemed</option>
              <option value="redeemed">Fully Redeemed</option>
              <option value="expired">Expired</option>
              <option value="blocked">Blocked</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          {/* Store */}
          <div className="w-40">
            <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1">Store</label>
            <select
              value={filterStoreId}
              onChange={e => { setFilterStoreId(e.target.value); setPage(0); }}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none bg-gray-50"
              data-testid="filter-store"
            >
              <option value="">All Stores</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          {/* Date Range */}
          <div className="w-[220px]">
            <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1">Date Range</label>
            <div className="flex items-center gap-1 border border-gray-200 rounded-xl px-3 py-2 bg-gray-50">
              <Calendar size={13} className="text-gray-400 flex-shrink-0" />
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }}
                className="outline-none bg-transparent text-xs text-gray-700 w-full" data-testid="date-from" />
              <span className="text-gray-300 mx-0.5">-</span>
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }}
                className="outline-none bg-transparent text-xs text-gray-700 w-full" data-testid="date-to" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
          <span className="text-xs text-gray-500">{total} voucher(s) found</span>
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-xl text-xs font-medium transition-colors disabled:opacity-50"
              data-testid="export-csv-btn"
            >
              <Download size={13} /> {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">{[...Array(8)].map((_, i) => <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-gray-50/80 border-b border-gray-100">
              <tr>
                {['Voucher Code', 'Customer', 'Initial Value', 'Balance', 'Status', 'Expiry Date', 'Issued On', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {vouchers.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-16 text-gray-400">
                  <Ticket size={32} className="mx-auto mb-2 text-gray-300" />
                  <p>No vouchers found</p>
                </td></tr>
              ) : vouchers.map(v => {
                const ds = getDisplayStatus(v);
                return (
                  <tr
                    key={v.id}
                    className="hover:bg-gray-50/60 cursor-pointer transition-colors"
                    onClick={() => onViewDetail?.(v.id)}
                    data-testid="voucher-row"
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-800 font-bold tracking-wider bg-gray-100 px-2 py-1 rounded">{v.code}</span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {v.recipient_name ? (
                        <div>
                          <p className="text-gray-700 font-medium">{v.recipient_name}</p>
                          <p className="text-gray-400">{v.recipient_email}</p>
                        </div>
                      ) : <span className="text-gray-300 italic">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3 font-heading font-bold text-[#561D1A] text-sm">{formatAmount(v.amount, activeCurrency)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-bold ${v.balance < v.amount ? 'text-orange-600' : 'text-gray-700'}`}>
                        {formatAmount(v.balance ?? v.amount, activeCurrency)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2.5 py-1 rounded-full font-semibold border ${statusColors[ds] || 'bg-gray-100 text-gray-600'}`}>
                        {statusLabels[ds] || v.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(v.expiry_date)}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">{formatDate(v.created_at)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); onViewDetail?.(v.id); }}
                        className="text-xs text-[#561D1A] hover:text-[#EC1A23] flex items-center gap-1 font-medium"
                        data-testid="view-detail-btn"
                      >
                        <Eye size={13} /> Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4" data-testid="pagination">
          <p className="text-xs text-gray-400">Page {page + 1} of {totalPages} ({total} total)</p>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30" data-testid="prev-page-btn">
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNum = page < 3 ? i : page - 2 + i;
              if (pageNum >= totalPages) return null;
              return (
                <button key={pageNum} onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 rounded-lg text-xs font-medium ${pageNum === page ? 'bg-[#561D1A] text-white' : 'border border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                  {pageNum + 1}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30" data-testid="next-page-btn">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
